import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  where
} from 'firebase/firestore'
import { db } from './config'
import { COLLECTIONS } from '@/constants'
import type { ApiResponse } from '@/types'
import { authService } from './auth'

export type ManagedUserRole = 'admin' | 'engineer'

export interface FieldEngineer {
  id: string
  name: string
  email: string
  phoneNumber: string
  location: string
  role: ManagedUserRole
  isActive: boolean
  uid?: string
  createdAt: Date
  updatedAt: Date
}

export type FieldEngineerInput = Omit<FieldEngineer, 'id' | 'createdAt' | 'updatedAt'>

const getDb = () => {
  if (!db) throw new Error('Firebase is not configured.')
  return db
}

export const engineerService = {
  async list(): Promise<ApiResponse<FieldEngineer[]>> {
    try {
      const q = query(collection(getDb(), COLLECTIONS.ENGINEERS), orderBy('name'))
      const snap = await getDocs(q)
      const engineers: FieldEngineer[] = snap.docs.map(d => ({
        id: d.id,
        ...(d.data() as Omit<FieldEngineer, 'id'>),
        role: (d.data().role ?? 'engineer') as ManagedUserRole,
        createdAt: d.data().createdAt?.toDate?.() ?? new Date(),
        updatedAt: d.data().updatedAt?.toDate?.() ?? new Date()
      }))
      return { success: true, data: engineers }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch engineers' }
    }
  },

  async findByPhone(phoneNumber: string): Promise<string | null> {
    try {
      const normalized = phoneNumber.startsWith('+91') ? phoneNumber : `+91${phoneNumber}`
      const q = query(collection(getDb(), COLLECTIONS.USERS), where('phoneNumber', '==', normalized))
      const snap = await getDocs(q)
      if (!snap.empty) return snap.docs[0].id
      return null
    } catch {
      return null
    }
  },

  async get(id: string): Promise<ApiResponse<FieldEngineer>> {
    try {
      const snap = await getDoc(doc(getDb(), COLLECTIONS.ENGINEERS, id))
      if (!snap.exists()) return { success: false, error: 'Engineer not found' }
      const data = snap.data()
      return {
        success: true,
        data: {
          id: snap.id,
          ...(data as Omit<FieldEngineer, 'id'>),
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date()
        }
      }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to fetch engineer' }
    }
  },

  async create(input: FieldEngineerInput): Promise<ApiResponse<FieldEngineer>> {
    try {
      const normalized = input.phoneNumber.startsWith('+91')
        ? input.phoneNumber
        : `+91${input.phoneNumber}`

      let uid = ''
      if (input.email) {
        try {
          uid = await authService.createEmailUser(input.email, input.name)
        } catch (authErr: any) {
          // If the email already exists in Firebase Auth, we shouldn't fail completely.
          // We can use the existing email, but we won't have the UID unless we handle it.
          // Typically in dev/prod, if it's already in use, throw a clear error.
          return { success: false, error: authErr instanceof Error ? authErr.message : 'Auth registration failed' }
        }
      }

      // Write to engineers collection (directory)
      const ref = await addDoc(collection(getDb(), COLLECTIONS.ENGINEERS), {
        ...input,
        phoneNumber: normalized,
        uid: uid || '',
        role: input.role ?? 'engineer',
        isActive: input.isActive ?? true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      // Also pre-register in users collection so role is set on first login
      const userPayload = {
        uid: uid || '',
        phoneNumber: normalized,
        name: input.name,
        email: input.email ?? '',
        role: input.role ?? 'engineer',
        location: input.location,
        isActive: input.isActive ?? true,
        preRegistered: true,
        engineerDocId: ref.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }

      if (uid) {
        // Document ID is the Firebase Auth UID
        await setDoc(doc(getDb(), COLLECTIONS.USERS, uid), userPayload)
      } else {
        // No email auth yet, will claim later via phone OTP
        await addDoc(collection(getDb(), COLLECTIONS.USERS), userPayload)
      }

      const created = await getDoc(ref)
      const data = created.data()!
      return {
        success: true,
        data: {
          id: created.id,
          ...(data as Omit<FieldEngineer, 'id'>),
          role: (data.role ?? 'engineer') as ManagedUserRole,
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date()
        }
      }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create user' }
    }
  },

  async update(id: string, input: Partial<FieldEngineerInput>): Promise<ApiResponse<void>> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.ENGINEERS, id), {
        ...input,
        updatedAt: serverTimestamp()
      })
      // Sync role/name change to users collection if a pre-registered doc exists
      if (input.phoneNumber || input.role || input.name) {
        const snap = await getDoc(doc(getDb(), COLLECTIONS.ENGINEERS, id))
        if (snap.exists()) {
          const phone = snap.data().phoneNumber
          const normalized = phone?.startsWith('+91') ? phone : `+91${phone}`
          const uq = query(collection(getDb(), COLLECTIONS.USERS), where('phoneNumber', '==', normalized))
          const usnap = await getDocs(uq)
          for (const udoc of usnap.docs) {
            await updateDoc(udoc.ref, {
              ...(input.name && { name: input.name }),
              ...(input.role && { role: input.role }),
              ...(input.email && { email: input.email }),
              updatedAt: serverTimestamp()
            })
          }
        }
      }
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update user' }
    }
  },

  async remove(id: string): Promise<ApiResponse<void>> {
    try {
      await deleteDoc(doc(getDb(), COLLECTIONS.ENGINEERS, id))
      return { success: true }
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete engineer' }
    }
  }
}
